import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/**
 * FeedbackLanding - Public page for patients to submit feedback
 * Accessed via /feedback/:token (no authentication required)
 */
function FeedbackLanding() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [comment, setComment] = useState('');
  const [reviewUrl, setReviewUrl] = useState(null);
  const [thankYouMessage, setThankYouMessage] = useState('');

  useEffect(() => {
    loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const response = await api.getFeedbackPageData(token);

      if (response.success && response.valid) {
        setPageData(response);
      } else if (response.alreadySubmitted) {
        setError('already_submitted');
      } else {
        setError(response.error || 'invalid_token');
      }
    } catch (err) {
      setError('load_failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingClick = async (rating) => {
    setSelectedRating(rating);
    setSubmitting(true);

    try {
      const response = await api.submitFeedbackRating(token, rating);

      if (response.success) {
        if (response.routeToReview && response.reviewUrl) {
          // High rating (4-5 stars): auto-redirect to the public review platform
          // (Google Reviews / Yelp / etc.) instead of making the patient click
          // an extra "Leave a Public Review" button. The /review/:token endpoint
          // logs the click and 302s to the configured platform URL.
          setThankYouMessage('Thank you! Redirecting you to leave a public review…');
          setReviewUrl(response.reviewUrl);
          setSubmitted(true);
          window.location.href = response.reviewUrl;
        } else if (response.showCommentForm) {
          setShowCommentForm(true);
          setThankYouMessage('We appreciate your feedback. Would you like to share more details?');
        }
      } else {
        setError(response.error || 'submit_failed');
      }
    } catch (err) {
      setError('submit_failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentSubmit = async () => {
    setSubmitting(true);

    try {
      const response = await api.submitFeedbackComment(token, comment);

      if (response.success) {
        setThankYouMessage('Thank you for your feedback. We will review it and work to improve.');
        setSubmitted(true);
        setShowCommentForm(false);
      }
    } catch (err) {
      // Still show thank you even if comment fails
      setThankYouMessage('Thank you for your feedback.');
      setSubmitted(true);
      setShowCommentForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipComment = () => {
    setThankYouMessage('Thank you for your feedback.');
    setSubmitted(true);
    setShowCommentForm(false);
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {error === 'already_submitted' ? (
            <>
              <h1 style={styles.title}>Thank You!</h1>
              <p style={styles.subtitle}>You have already submitted your feedback. We appreciate it!</p>
            </>
          ) : error === 'invalid_token' ? (
            <>
              <h1 style={styles.title}>Invalid Link</h1>
              <p style={styles.subtitle}>This feedback link is no longer valid or has expired.</p>
            </>
          ) : (
            <>
              <h1 style={styles.title}>Something went wrong</h1>
              <p style={styles.subtitle}>Please try again later.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Thank you state (after submission)
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {pageData?.practice?.logoUrl && (
            <img
              src={pageData.practice.logoUrl}
              alt={pageData.practice.name}
              style={styles.logo}
            />
          )}
          <h1 style={styles.title}>{selectedRating >= 4 ? 'Thank You!' : 'Thank You'}</h1>
          {selectedRating >= 4 && (
            <div style={styles.starDisplay}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  style={{
                    ...styles.starLarge,
                    color: star <= selectedRating ? '#fbbf24' : '#d1d5db'
                  }}
                >
                  ★
                </span>
              ))}
            </div>
          )}
          <p style={styles.subtitle}>{thankYouMessage}</p>
          {reviewUrl && (
            <a href={reviewUrl} style={styles.reviewButton}>
              Leave a Public Review
            </a>
          )}
        </div>
      </div>
    );
  }

  // Comment form (for low ratings)
  if (showCommentForm) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {pageData?.practice?.logoUrl && (
            <img
              src={pageData.practice.logoUrl}
              alt={pageData.practice.name}
              style={styles.logo}
            />
          )}
          <h1 style={styles.title}>We Value Your Feedback</h1>
          <p style={styles.subtitle}>{thankYouMessage}</p>
          <div style={styles.starDisplay}>
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                style={{
                  ...styles.starSmall,
                  color: star <= selectedRating ? '#fbbf24' : '#d1d5db'
                }}
              >
                ★
              </span>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us what we could improve (optional)"
            style={styles.textarea}
            rows={4}
          />
          <div style={styles.buttonGroup}>
            <button
              onClick={handleCommentSubmit}
              disabled={submitting}
              style={styles.primaryButton}
            >
              {submitting ? 'Sending...' : 'Submit Feedback'}
            </button>
            <button
              onClick={handleSkipComment}
              disabled={submitting}
              style={styles.secondaryButton}
            >
              Skip
            </button>
          </div>
          <p style={styles.privacyNote}>
            This feedback is private and will be reviewed by our team.
          </p>
        </div>
      </div>
    );
  }

  // Main rating view
  const practiceName = pageData?.practice?.name || 'our practice';
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {pageData?.practice?.logoUrl && (
          <img
            src={pageData.practice.logoUrl}
            alt={practiceName}
            style={styles.logo}
          />
        )}
        <h1 style={styles.title}>How was your visit?</h1>
        <p style={styles.subtitle}>We'd love to hear about your experience at {practiceName}</p>

        <div style={styles.starsContainer}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => handleRatingClick(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              disabled={submitting}
              style={{
                ...styles.starButton,
                color: star <= (hoveredRating || selectedRating) ? '#fbbf24' : '#d1d5db',
                transform: star <= hoveredRating ? 'scale(1.1)' : 'scale(1)'
              }}
              aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
            >
              ★
            </button>
          ))}
        </div>

        {submitting && <p style={styles.submitting}>Submitting...</p>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    padding: '20px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '40px',
    maxWidth: '450px',
    width: '100%',
    textAlign: 'center'
  },
  logo: {
    maxWidth: '150px',
    maxHeight: '60px',
    marginBottom: '24px',
    objectFit: 'contain'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    margin: '0 0 32px 0',
    lineHeight: '1.5'
  },
  starsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px'
  },
  starButton: {
    fontSize: '48px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    padding: '4px'
  },
  starDisplay: {
    display: 'flex',
    justifyContent: 'center',
    gap: '4px',
    marginBottom: '16px'
  },
  starLarge: {
    fontSize: '36px'
  },
  starSmall: {
    fontSize: '24px'
  },
  loading: {
    color: '#6b7280',
    padding: '40px'
  },
  submitting: {
    color: '#6b7280',
    fontSize: '14px'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '16px',
    resize: 'vertical',
    marginBottom: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginBottom: '16px'
  },
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: 'white',
    color: '#4b5563',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  reviewButton: {
    display: 'inline-block',
    padding: '14px 28px',
    backgroundColor: '#2563eb',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    marginTop: '16px',
    transition: 'background-color 0.2s'
  },
  privacyNote: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: 0
  }
};

export default FeedbackLanding;
